import React, { useState, useEffect, useRef } from "react";
import api from "../api/api";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import Header from "./Header";

export default function UserTable() {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [verifyingIndex, setVerifyingIndex] = useState(null);
    const [areas, setAreas] = useState([]);
    const [areaLoading, setAreaLoading] = useState(false);

    // Keep track of search term and dropdown visibility per row
    const [searchTerms, setSearchTerms] = useState({});
    const [showDropdowns, setShowDropdowns] = useState({});
    const [inputValues, setInputValues] = useState({});
    const [activeIndex, setActiveIndex] = useState({}); // per row active index
    const areaRefs = useRef({}); // refs for click outside
    const listRefs = useRef({}); // refs for dropdown lists

    // Fetch customers from API
    const fetchCustomers = async () => {
        setLoading(true);
        try {
            const username = sessionStorage.getItem("username") || "UnknownUser";
            const response = await api.get("OnlineNewCustomers", { user: username });
            const data = response?.OnlineNewCustomersjs?.Table || [];

            const mappedUsers = data.map((item) => ({
                id: item.Vid,
                name: item.Vname || "-",
                phone: item.VCode || "-",
                mail: item.Mail || "-",
                address: `${item.Address1 || ""} ${item.Address2 || ""}`.trim() || "-",
                city: item.City || "-",
                pincode: item.Pincode || "-",
                division: item.Division || "-",
                area: item.City || "-", // area initially same as city
                password: item.VPassword || "",
                verify: false,
            }));

            setUsers(mappedUsers);
        } catch (error) {
            console.error("Error fetching customers:", error);
            alert("Failed to fetch customers.");
        } finally {
            setLoading(false);
        }
    };

    // Fetch areas from API
    const fetchAreas = async () => {
        setAreaLoading(true);
        try {
            const response =
                (await api.GetAreaList?.()) || (await api.get("GetAreaList", {}));
            const list =
                response?.data?.GetAreaListjs?.Table ||
                response?.GetAreaListjs?.Table ||
                response?.data?.Table ||
                [];
            setAreas(list);

        } catch (error) {
            console.error("❌ Error fetching areas:", error);
        } finally {
            setAreaLoading(false);
        }
    };

    useEffect(() => {
        fetchCustomers();
        fetchAreas();
    }, []);


    useEffect(() => {
        const handleClickOutside = (event) => {
            Object.keys(areaRefs.current).forEach((key) => {
                if (key.startsWith("input-")) {
                    const idx = key.split("-")[1];
                    const inputRef = areaRefs.current[key];
                    const dropdownRef = areaRefs.current[`dropdown-${idx}`];
                    if (
                        inputRef &&
                        !inputRef.contains(event.target) &&
                        (!dropdownRef || !dropdownRef.contains(event.target))
                    ) {
                        setShowDropdowns((prev) => ({ ...prev, [idx]: false }));
                        setActiveIndex((prev) => ({ ...prev, [idx]: -1 }));
                    }
                }
            });
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Auto scroll for each row's dropdown
    useEffect(() => {
        Object.keys(activeIndex).forEach((rowIndex) => {
            const idx = parseInt(rowIndex);
            const active = activeIndex[idx];
            if (active >= 0 && listRefs.current[idx]) {
                const item = listRefs.current[idx].children[active];
                item?.scrollIntoView({ block: "nearest" });
            }
        });
    }, [activeIndex]);

    const handleSelectArea = (index, selectedCity) => {
        // Find the selected area object
        const selectedArea = areas.find(a => a.city === selectedCity);
        if (!selectedArea) return;

        const updatedUsers = [...users];
        updatedUsers[index].area = selectedArea.city; // ✅ update area
        updatedUsers[index].division = selectedArea.Division; // ✅ update division
        setUsers(updatedUsers);

        // ✅ update input value to show selected area
        setInputValues((prev) => ({ ...prev, [index]: selectedArea.city }));

        // ✅ close this dropdown
        setShowDropdowns((prev) => ({ ...prev, [index]: false }));
    };


    const handleVerify = async (index) => {
        const customer = users[index];
        if (!customer) return;

        setVerifyingIndex(index);

        const payload = {
            Items: [
                {
                    Vid: customer.id,
                    Vcode: customer.phone || "NA",
                    Vname: customer.name || "NA",
                    Mail: customer.mail || "NA",
                    Address1: customer.address || "NA",
                    Address2: "-",
                    City: customer.area || customer.city || "NA",
                    Division: customer.division || "NA",
                    Pincode: customer.pincode || "000000",
                    VPassword: customer.password
                },
            ],
            UserType: "New User", // Or dynamic if needed
        };

        console.log("📦 Payload to OnlineCustomerVerification:", payload);

        try {
            const response = await api.OnlineCustomerVerification(payload);
            console.log("✅ OnlineCustomerVerification Response:", response?.data);

            // Check if API returned successfully
            if (response?.data?.OnlineCustomerVerify) {
                const updatedUsers = [...users];
                updatedUsers[index].verify = true;
                setUsers(updatedUsers);
            } else {
                alert(" Verification Success!");
            }

            // ✅ Refresh the page after confirmation
             window.location.reload();

        } catch (error) {
            console.error("❌ Error verifying customer:", error);
            alert("Failed to verify customer. Check console for details.");
        } finally {
            setVerifyingIndex(null);
        }
    };

    // Export users to Excel
    const exportToExcel = () => {
        if (users.length === 0) {
            alert("No data to export!");
            return;
        }

        const dataToExport = users.map((u) => ({
            Name: u.name,
            Phone: u.phone,
            Mail: u.mail,
            Address: u.address,
            Area: u.area,
            Pincode: u.pincode,
            Division: u.division,
            Verified: u.verify ? "Yes" : "No",
        }));

        const worksheet = XLSX.utils.json_to_sheet(dataToExport);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Customers");

        const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
        const data = new Blob([excelBuffer], { type: "application/octet-stream" });
        saveAs(data, `Customers_${new Date().toISOString().slice(0, 10)}.xlsx`);
    };

    const handleKeyDownArea = (rowIndex) => (e) => {
        const options = areas.filter((a) => {
            const term = (searchTerms[rowIndex] || "").toLowerCase().trim();
            if (term === "") return true;
            return (a.city || "").toLowerCase().includes(term);
        });
        if (!showDropdowns[rowIndex] && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
            setShowDropdowns((prev) => ({ ...prev, [rowIndex]: true }));
            setActiveIndex((prev) => ({ ...prev, [rowIndex]: 0 }));
            return;
        }

        switch (e.key) {
            case "ArrowDown":
                e.preventDefault();
                setActiveIndex((prev) => ({
                    ...prev,
                    [rowIndex]: prev[rowIndex] < options.length - 1 ? prev[rowIndex] + 1 : 0
                }));
                break;
            case "ArrowUp":
                e.preventDefault();
                setActiveIndex((prev) => ({
                    ...prev,
                    [rowIndex]: prev[rowIndex] > 0 ? prev[rowIndex] - 1 : options.length - 1
                }));
                break;
            case "Enter":
                if (showDropdowns[rowIndex] && activeIndex[rowIndex] >= 0) {
                    e.preventDefault();
                    const selected = options[activeIndex[rowIndex]];
                    if (selected) {
                        handleSelectArea(rowIndex, selected.city);
                        setActiveIndex((prev) => ({ ...prev, [rowIndex]: -1 }));
                    }
                }
                break;
            case "Escape":
                setShowDropdowns((prev) => ({ ...prev, [rowIndex]: false }));
                setActiveIndex((prev) => ({ ...prev, [rowIndex]: -1 }));
                break;
            default:
                break;
        }
    };

    const handleBack = () => window.history.back();

    const handleLogout = () => {
        sessionStorage.removeItem("isLoggedIn");
        sessionStorage.removeItem("username");
        sessionStorage.removeItem("lastVisited");
        window.location.href = "/";
    };

    return (
        <div className="min-h-screen bg-gray-50">
            <Header onLogout={handleLogout} />
            <main className="p-6 md:ml-64">
                {/* Top Bar */}
                <div className="flex items-center justify-between mb-6">
                    {/* Back Button */}
                    <button
                        onClick={handleBack}
                        className="inline-flex items-center justify-center px-3 py-1 font-semibold text-gray-800 transition bg-gray-200 rounded-full shadow-md hover:bg-gray-300"
                    >
                        ← Back
                    </button>

                    {/* Centered Title */}
                    <h2 className="text-2xl font-bold text-orange-600 text-center flex-1">
                        Verify Customers
                    </h2>

                    {/* Export Button */}
                    <button
                        onClick={exportToExcel}
                        className="px-3 py-1 font-semibold text-white bg-blue-500 hover:bg-blue-700 rounded-full shadow transition"
                    >
                        📄 Export Excel
                    </button>
                </div>

                {loading ? (
                    <div className="text-black font-semibold">Loading customers...</div>
                ) : (

                    <div className="overflow-x-auto p-4">

                        <table className="min-w-full bg-white  border-gray-300 rounded-2xl shadow-lg overflow-hidden">

                            <thead className="bg-orange-500 text-white">
                                <tr>
                                    <th className="px-4 py-2 text-left border-b">Name</th>
                                    <th className="px-4 py-2 text-left border-b">Phone</th>
                                    <th className="px-4 py-2 text-left border-b">Mail</th>
                                    <th className="px-4 py-2 text-left border-b">Address</th>
                                    <th className="px-4 py-2 text-left border-b">Area</th>
                                    <th className="px-4 py-2 text-left border-b">Pincode</th>
                                    <th className="px-4 py-2 text-left border-b">Division</th>
                                    <th className="px-4 py-2 text-left border-b">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {users.map((user, index) => {
                                    const filteredAreas = areas.filter((a) =>
                                        a.city.toLowerCase().includes(
                                            (searchTerms[index] || "").toLowerCase()
                                        )
                                    );
                                    return (
                                        <tr
                                            key={index}
                                            className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}
                                        >
                                            <td className="px-4 py-2 border-b text-black font-medium">{user.name}</td>
                                            <td className="px-4 py-2 border-b text-black">{user.phone}</td>
                                            <td className="px-4 py-2 border-b text-black">{user.mail}</td>
                                            <td className="px-4 py-2 border-b text-black">{user.address}</td>

                                            <td className="px-4 py-2 border-b text-black">
                                                <input
                                                    ref={(el) => (areaRefs.current[`input-${index}`] = el)}
                                                    type="text"
                                                    // ✅ Always prefer inputValues if exists, else user's area
                                                    value={
                                                        inputValues[index] !== undefined
                                                            ? inputValues[index]
                                                            : user.area || ""
                                                    }
                                                    onChange={(e) => {
                                                        const value = e.target.value;
                                                        setInputValues((prev) => ({ ...prev, [index]: value }));
                                                        setSearchTerms((prev) => ({ ...prev, [index]: value }));
                                                        setShowDropdowns((prev) => ({ ...prev, [index]: true }));
                                                        setActiveIndex((prev) => ({ ...prev, [index]: -1 }));
                                                    }}
                                                    onFocus={() =>
                                                        setShowDropdowns((prev) => ({ ...prev, [index]: true }))
                                                    }
                                                    onKeyDown={handleKeyDownArea(index)}
                                                    placeholder={areaLoading ? "Loading..." : "Search area..."}
                                                    className="p-2 text-sm border rounded-md w-full focus:ring-1 focus:ring-orange-400"
                                                    disabled={areaLoading}
                                                    autoComplete="off"
                                                />

                                                {/* Floating dropdown rendered outside table */}
                                                {showDropdowns[index] && areaRefs.current[`input-${index}`] && (
                                                    <ul
                                                        ref={(el) => {
                                                            areaRefs.current[`dropdown-${index}`] = el;
                                                            listRefs.current[index] = el;
                                                        }}
                                                        className="fixed max-h-56 overflow-y-auto bg-white border rounded-md shadow-lg z-[9999]"
                                                        style={{
                                                            top:
                                                                areaRefs.current[`input-${index}`].getBoundingClientRect().bottom +
                                                                window.scrollY +
                                                                2,
                                                            left:
                                                                areaRefs.current[`input-${index}`].getBoundingClientRect().left +
                                                                window.scrollX,
                                                            width: areaRefs.current[`input-${index}`].offsetWidth,
                                                        }}
                                                    >
                                                        {areas
                                                            .filter((a) => {
                                                                const term = (searchTerms[index] || "").toLowerCase().trim();
                                                                if (term === "") return true; // show all when no typing
                                                                return (a.city || "").toLowerCase().includes(term);
                                                            })
                                                            .map((item, idx) => (
                                                                <li
                                                                    key={idx}
                                                                    onClick={() => {
                                                                        handleSelectArea(index, item.city);
                                                                        setActiveIndex((prev) => ({ ...prev, [index]: -1 }));
                                                                    }}
                                                                    onMouseEnter={() => setActiveIndex((prev) => ({ ...prev, [index]: idx }))}
                                                                    className={`p-2 text-sm cursor-pointer ${
                                                                        idx === activeIndex[index]
                                                                            ? "bg-gray-100 font-semibold"
                                                                            : "hover:bg-orange-100"
                                                                    }`}
                                                                >
                                                                    {item.city}
                                                                </li>
                                                            ))}
                                                        {areas.length === 0 && (
                                                            <li className="p-2 text-sm text-gray-500">No areas found</li>
                                                        )}
                                                    </ul>
                                                )}
                                            </td>




                                            <td className="px-4 py-2 border-b text-black">{user.pincode}</td>
                                            <td className="px-4 py-2 border-b text-black">{user.division}</td>
                                            <td className="px-4 py-2 border-b text-center">
                                                {user.verify ? (
                                                    <span className="bg-orange-100 text-orange-600 px-2 py-1 rounded-full font-semibold">
                                                        Verified
                                                    </span>
                                                ) : (
                                                    <button
                                                        className="bg-orange-500 text-white px-2 py-1 rounded-full hover:bg-orange-600 transition font-semibold"
                                                        onClick={() => handleVerify(index)}
                                                        disabled={verifyingIndex === index}
                                                    >
                                                        {verifyingIndex === index ? "Verifying..." : "Verify"}
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                                {users.length === 0 && (
                                    <tr>
                                        <td colSpan="8" className="text-center p-4 text-black">
                                            No customers found.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </main>
        </div>
    );
}
